// Low-poly toy pieces from the mockup (scene.js): houses, tile buildings, gates, the rocket and vehicles.
// Every piece is baked into a GeometryBatch, never added as its own mesh.
import {
  BoxGeometry,
  ConeGeometry,
  CylinderGeometry,
  DodecahedronGeometry,
  ExtrudeGeometry,
  Matrix4,
  Shape,
  SphereGeometry,
  TorusGeometry,
  Vector3,
  type BufferGeometry,
} from 'three';
import type { ExtraPuff } from '../world/decor';
import { worldX, worldZ, type Island, type Tile } from '../world/island';
import type { VehicleKind } from '../world/traffic';
import { GeometryBatch } from './batch';
import { C } from './palette';

export const box = (w: number, h: number, d: number): BufferGeometry => new BoxGeometry(w, h, d);

function gable(w: number, d: number, h: number): BufferGeometry {
  const s = new Shape();
  s.moveTo(-d / 2 - 0.04, 0);
  s.lineTo(0, h);
  s.lineTo(d / 2 + 0.04, 0);
  s.lineTo(-d / 2 - 0.04, 0);
  const geo = new ExtrudeGeometry(s, { depth: w + 0.08, bevelEnabled: false });
  geo.translate(0, 0, -(w + 0.08) / 2);
  geo.rotateY(Math.PI / 2);
  return geo;
}

interface HouseOptions {
  readonly door?: boolean;
}

/** Wooden base, cream walls, corner beams and a gabled roof with eaves (PRD section 14.3). */
export function house(b: GeometryBatch, x: number, y: number, z: number, w: number, d: number, h: number, roof: string, opts: HouseOptions = {}): void {
  b.group({ position: [x, y, z] }, () => {
    b.add(box(w, 0.05, d), C.timber, { position: [0, 0.025, 0] });
    b.add(box(w * 0.96, h, d * 0.96), C.wall, { position: [0, 0.05 + h / 2, 0] });
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) b.add(box(0.03, h, 0.03), C.timber, { position: [sx * w * 0.47, 0.05 + h / 2, sz * d * 0.47] });
    }
    b.add(box(w * 0.97, 0.025, d * 0.97), C.timber, { position: [0, 0.05 + h * 0.55, 0] });
    b.add(gable(w, d, h * 0.75), roof, { position: [0, 0.05 + h, 0] });
    const windows = Math.max(1, Math.round(w / 0.18));
    for (let i = 0; i < windows; i++) {
      b.add(box(0.06, 0.07, 0.01), C.window, { position: [-w / 2 + ((i + 0.5) * w) / windows, 0.05 + h * 0.3, d * 0.485] });
    }
    if (opts.door) b.add(box(0.08, 0.13, 0.012), C.timber, { position: [0, 0.115, d * 0.487] });
  });
}

/** Scale applied to buildings on the map so they read from the tilted camera, as in the mockup. */
const BUILDING_SCALE = 1.35;

/**
 * Adds the representative building of a tile and returns its smoke or steam puffs,
 * which are instanced with the clouds.
 */
export function building(b: GeometryBatch, island: Island, tile: Tile, top: number): ExtraPuff[] {
  const first = tile.cells[0];
  if (!first) return [];
  const [c, r] = first;
  const x = worldX(island, c);
  const z = worldZ(island, r);
  const merged = tile.kind === 'taller';
  const cx = merged ? worldX(island, 9.5) : x;
  const cz = merged ? worldZ(island, 5.5) : z;
  const scaleAround = new Matrix4()
    .makeTranslation(cx, top, cz)
    .multiply(new Matrix4().makeScale(BUILDING_SCALE, BUILDING_SCALE, BUILDING_SCALE))
    .multiply(new Matrix4().makeTranslation(-cx, -top, -cz));
  const puffs: ExtraPuff[] = [];
  const addPuff = (px: number, py: number, pz: number, size: number): void => {
    const p = new Vector3(px, py, pz).applyMatrix4(scaleAround);
    puffs.push({ cell: [c, r], x: p.x, y: p.y, z: p.z, size: size * BUILDING_SCALE });
  };
  b.group(scaleAround, () => {
    switch (tile.kind) {
      case 'mina': {
        house(b, x - 0.18, top, z + 0.18, 0.32, 0.26, 0.16, C.roofBlue, { door: true });
        b.group({ position: [x + 0.2, top, z + 0.05] }, () => {
          for (const s of [-1, 1]) b.add(box(0.03, 0.5, 0.03), C.timber, { position: [s * 0.08, 0.24, 0], rotation: [0, 0, -s * 0.18] });
          b.add(box(0.2, 0.03, 0.04), C.timber, { position: [0, 0.4, 0] });
          b.add(new TorusGeometry(0.06, 0.012, 6, 12), C.metal, { position: [0, 0.47, 0] });
        });
        const ore = tile.ore === 'copper' ? C.oreCopper : C.ore;
        for (let i = 0; i < 4; i++) {
          b.add(new DodecahedronGeometry(0.05, 0), ore, { position: [x + 0.28 + (i % 2) * 0.07, top + 0.04 + Math.floor(i / 2) * 0.05, z + 0.32 - (i % 2) * 0.05] });
        }
        break;
      }
      case 'fundicion':
        house(b, x - 0.05, top, z + 0.08, 0.5, 0.36, 0.2, C.roofOrange, { door: true });
        b.add(new CylinderGeometry(0.06, 0.075, 0.6, 8), C.stone, { position: [x + 0.27, top + 0.3, z - 0.22] });
        b.add(box(0.09, 0.07, 0.01), '#ffb347', { position: [x - 0.15, top + 0.13, z + 0.267] }, { emissive: '#ff8a00', emissiveIntensity: 1 });
        addPuff(x + 0.28, top + 0.72, z - 0.22, 0.07);
        addPuff(x + 0.34, top + 0.84, z - 0.27, 0.055);
        break;
      case 'taller': {
        // Long hall on the bottom row of the joined L, and a glass dome on the top cell.
        const hx = worldX(island, 9.5);
        const hz = worldZ(island, 6);
        house(b, hx, top, hz + 0.02, 1.45, 0.5, 0.22, C.roofBlue, { door: true });
        b.add(new SphereGeometry(0.26, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2), C.glass, { position: [x, top + 0.04, z + 0.05] }, { smooth: true });
        b.add(new CylinderGeometry(0.28, 0.28, 0.05, 16), C.wall, { position: [x, top + 0.025, z + 0.05] });
        b.add(new TorusGeometry(0.07, 0.025, 6, 8), '#3aa76d', { position: [hx, top + 0.18, hz + 0.27] });
        break;
      }
      case 'lab':
        b.add(new CylinderGeometry(0.3, 0.32, 0.08, 16), C.wall, { position: [x, top + 0.04, z] });
        b.add(new SphereGeometry(0.27, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2), '#f7f7f2', { position: [x, top + 0.08, z] }, { smooth: true });
        b.add(new TorusGeometry(0.2, 0.02, 6, 20), C.roofBlue, { position: [x, top + 0.2, z], rotation: [Math.PI / 2, 0, 0] });
        b.add(new CylinderGeometry(0.012, 0.012, 0.3, 5), C.metal, { position: [x + 0.3, top + 0.15, z - 0.25] });
        b.add(new SphereGeometry(0.09, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2.5), '#ffffff', { position: [x + 0.3, top + 0.32, z - 0.25], rotation: [Math.PI * 0.7, 0, 0] }, { doubleSided: true });
        break;
      case 'bomba':
        // Wind pump tower, a nod to the reference's windmill.
        b.add(new CylinderGeometry(0.12, 0.17, 0.5, 8), C.stone, { position: [x, top + 0.25, z] });
        b.add(new ConeGeometry(0.17, 0.2, 8), C.roofBlue, { position: [x, top + 0.6, z] });
        b.group({ position: [x, top + 0.42, z + 0.18] }, () => {
          for (let i = 0; i < 4; i++) {
            b.group({ rotation: [0, 0, i * (Math.PI / 2) + 0.4] }, () => {
              b.add(box(0.05, 0.3, 0.01), C.wall, { position: [0, 0.15, 0] });
            });
          }
        });
        b.add(new CylinderGeometry(0.02, 0.02, 0.5, 6), C.metal, { position: [x + 0.25, top + 0.03, z], rotation: [0, 0, Math.PI / 2] });
        break;
      case 'muelle':
        for (let i = 0; i < 6; i++) b.add(box(0.08, 0.025, 0.24), '#b07a4c', { position: [x - 0.5 - i * 0.075, top - 0.03, z + 0.1] });
        for (const s of [-1, 1]) b.add(new CylinderGeometry(0.018, 0.018, 0.3, 5), C.timber, { position: [x - 0.9, top - 0.15, z + 0.1 + s * 0.1] });
        b.add(box(0.14, 0.12, 0.14), '#c98a4b', { position: [x + 0.05, top + 0.06, z + 0.15] });
        b.add(box(0.12, 0.1, 0.12), C.roofBlue, { position: [x + 0.22, top + 0.05, z + 0.12] });
        b.add(box(0.035, 0.55, 0.035), C.roofOrange, { position: [x - 0.25, top + 0.27, z - 0.25] });
        b.add(box(0.4, 0.035, 0.035), C.roofOrange, { position: [x - 0.42, top + 0.53, z - 0.25] });
        break;
    }
  });
  return puffs;
}

/** A small glowing arch on a tile edge: blue for entrances, orange for exits. */
export function gate(b: GeometryBatch, x: number, y: number, z: number, dir: readonly [number, number], kind: 'in' | 'out', scale = 0.8): void {
  const col = kind === 'in' ? C.portIn : C.portOut;
  const glow = { emissive: col, emissiveIntensity: 0.35 };
  b.group({ position: [x, y, z], rotation: [0, Math.atan2(dir[0], dir[1]), 0], scale }, () => {
    b.add(box(0.05, 0.22, 0.05), col, { position: [-0.13, 0.11, 0] }, glow);
    b.add(box(0.05, 0.22, 0.05), col, { position: [0.13, 0.11, 0] }, glow);
    b.add(box(0.33, 0.06, 0.07), col, { position: [0, 0.24, 0] }, glow);
    b.add(new ConeGeometry(0.055, 0.1, 3), '#ffffff', { position: [0, 0.36, 0], rotation: [kind === 'out' ? Math.PI / 2 : -Math.PI / 2, 0, 0] }, { emissive: '#ffffff', emissiveIntensity: 0.3 });
  });
}

/** The Proyecto Lanzamiento rocket on its pad, visible from the start (PRD section 4). */
export function rocket(b: GeometryBatch, x: number, y: number, z: number): void {
  b.add(new CylinderGeometry(0.34, 0.38, 0.05, 12), '#9aa3b0', { position: [x, y + 0.025, z] });
  b.add(new CylinderGeometry(0.075, 0.08, 0.6, 10), '#f7f7f2', { position: [x, y + 0.36, z] }, { smooth: true });
  b.add(new ConeGeometry(0.075, 0.2, 10), C.roofOrange, { position: [x, y + 0.76, z] });
  for (let i = 0; i < 3; i++) {
    b.add(box(0.02, 0.14, 0.12), C.roofBlue, { position: [x + Math.sin(i * 2.1) * 0.09, y + 0.12, z + Math.cos(i * 2.1) * 0.09], rotation: [0, i * 2.1, 0] });
  }
}

/** Geometry of one vehicle, centred on its own origin and facing +z. */
export function vehicleGeometry(kind: VehicleKind): BufferGeometry {
  const b = new GeometryBatch();
  switch (kind) {
    case 'truck':
      b.add(box(0.13, 0.08, 0.1), C.roofBlue, { position: [0, 0.06, 0.09] });
      b.add(box(0.14, 0.06, 0.18), C.timber, { position: [0, 0.05, -0.06] });
      b.add(new DodecahedronGeometry(0.05, 0), C.ore, { position: [0, 0.1, -0.06] });
      break;
    case 'loco':
      b.add(box(0.15, 0.1, 0.24), C.roofOrange, { position: [0, 0.07, 0] });
      b.add(box(0.15, 0.06, 0.08), C.dark, { position: [0, 0.15, -0.06] });
      b.add(new CylinderGeometry(0.025, 0.025, 0.08, 6), C.dark, { position: [0, 0.16, 0.08] });
      break;
    case 'wagon':
      b.add(box(0.15, 0.08, 0.2), C.roofBlue, { position: [0, 0.06, 0] });
      b.add(new DodecahedronGeometry(0.05, 0), C.oreCopper, { position: [0, 0.12, 0] });
      break;
    case 'boat': {
      const s = new Shape();
      s.moveTo(-0.09, -0.14);
      s.lineTo(0.09, -0.14);
      s.lineTo(0.09, 0.08);
      s.lineTo(0, 0.18);
      s.lineTo(-0.09, 0.08);
      s.lineTo(-0.09, -0.14);
      const hull = new ExtrudeGeometry(s, { depth: 0.07, bevelEnabled: false });
      hull.rotateX(-Math.PI / 2);
      b.add(hull, '#ffffff');
      b.add(box(0.11, 0.07, 0.1), C.roofBlue, { position: [0, 0.1, -0.05] });
      break;
    }
    case 'barge':
      b.add(box(0.16, 0.05, 0.3), '#9c6a43', { position: [0, 0.02, 0] });
      b.add(box(0.1, 0.07, 0.08), C.roofBlue, { position: [0, 0.08, 0.07] });
      b.add(box(0.1, 0.07, 0.08), '#4aa6ff', { position: [0, 0.08, -0.06] });
      break;
    case 'drone':
      b.add(box(0.1, 0.05, 0.1), C.roofOrange);
      for (const [dx, dz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) {
        b.add(box(0.02, 0.01, 0.1), C.metal, { position: [dx * 0.05, 0.02, dz * 0.05], rotation: [0, (Math.PI / 4) * dx * dz, 0] });
        b.add(new CylinderGeometry(0.045, 0.045, 0.008, 12), '#e9f1fb', { position: [dx * 0.09, 0.035, dz * 0.09] });
      }
      b.add(box(0.05, 0.05, 0.05), '#a974e0', { position: [0, -0.05, 0] });
      break;
  }
  return b.build();
}
